import { css } from "@emotion/react";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import { ChannelSchema } from "@polifonia/interlude/models";
import { theme } from "@polifonia/theme";
import { useGateValue, useStatsigClient } from "@statsig/react-bindings";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { Typography } from "@/components/Typography";
import { useEnableEmailChannelSelfOnboarding } from "@/features/email/useEnableEmailChannelSelfOnboarding";
import { useEnableLineChannelCreation } from "@/features/line/useEnableLineChannelCreation";
import { useEnableWebstream } from "@/features/webstream/useEnableWebstream";
import type { InterludeTypes } from "@/interlude";
import { defineStyles } from "@/internal/emotion";
import { ChannelIcon } from "@/pages/channels/components/ChannelIcon";
import { getChannelSelectFooterAction } from "@/pages/channels/components/getChannelSelectFooterAction";

type ChannelTypes = InterludeTypes["Channel"]["type"] | "web-streams";

const isValidChannelType = (value: string): value is ChannelTypes => {
  return (
    ChannelSchema.shape.type.safeParse(value).success || value === "web-streams"
  );
};

interface ChannelSelectModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (channelType: ChannelTypes) => void;
  loading?: boolean;
  wccsAvailableChannelCount?: number;
  wccsWebStreamsModuleAvailable?: boolean;
  /**
   * Option selected when the modal opens, e.g. from a deep link. Read only
   * on the closed-to-open transition; WhatsApp is used when absent.
   */
  preselectedChannelType?: ChannelTypes | undefined;
}

const styles = {
  RadioGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
} satisfies Record<string, SxProps>;

export const ChannelSelectModal: React.FC<ChannelSelectModalProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false,
  wccsAvailableChannelCount = 0,
  wccsWebStreamsModuleAvailable = false,
  preselectedChannelType,
}) => {
  const { t } = useTranslation();
  const [channelType, setChannelType] = useState<ChannelTypes>("whatsapp");
  // Each opening starts from the caller's preselection, or WhatsApp when there
  // is none. Done during render on the closed-to-open transition so the
  // previous opening's choice never shows for a frame.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setChannelType(preselectedChannelType ?? "whatsapp");
  }
  const handleChannelTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    if (!isValidChannelType(value)) return;
    setChannelType(value);
  };
  const { client: statsigClient } = useStatsigClient();
  const enableEmailChannels = useGateValue("enable_email_channels");
  const enableWebstream = useEnableWebstream();
  const enableEmailChannelSelfOnboarding =
    useEnableEmailChannelSelfOnboarding();
  const enableLineChannelCreation = useEnableLineChannelCreation();
  const channelMigration = statsigClient.checkGate("channel_migration");
  const footerAction = getChannelSelectFooterAction({
    channelType,
    enableEmailChannelSelfOnboarding,
    enableLineChannelCreation,
    wccsAvailableChannelCount,
    wccsWebStreamsModuleAvailable,
  });
  return (
    <Modal
      open={open}
      handleClose={onClose}
      title={t("channels.addModal.title")}
      footer={
        <Stack direction="row" justifyContent="flex-end" gap="12px">
          <Button variant="plain" onClick={onClose} disabled={loading}>
            {t("channels.addModal.footer.cancel")}
          </Button>
          {footerAction === "submitRequest" ? (
            <Button variant="primary" href={t("newRequest.email.link")}>
              {t("channels.addModal.footer.submitRequest")}
            </Button>
          ) : footerAction === "contactUs" ? (
            <Button variant="primary" href={t("newRequest.link")}>
              {t("channels.addModal.footer.contactUs")}
            </Button>
          ) : (
            <Button
              variant="primary"
              loading={loading}
              onClick={() => onSubmit(channelType)}
            >
              {t("channels.addModal.footer.continue")}
            </Button>
          )}
        </Stack>
      }
    >
      <Stack gap="16px">
        <FormControl>
          <RadioGroup
            name="channel-buttons-group"
            value={channelType}
            onChange={handleChannelTypeChange}
            sx={styles.RadioGroup}
          >
            {channelMigration ? (
              <>
                <FormControlLabel
                  value="fb"
                  control={<Radio />}
                  label={
                    <ChannelRadioGroupOption
                      channelType="fb"
                      title={t("channels.addModal.body.channel.fb.title")}
                    />
                  }
                />
                <FormControlLabel
                  value="ig"
                  control={<Radio />}
                  label={
                    <ChannelRadioGroupOption
                      channelType="ig"
                      title={t("channels.addModal.body.channel.ig.title")}
                    />
                  }
                />
              </>
            ) : null}
            {/* The LINE creation gate opens LINE to orgs outside the channel
                migration, so LINE no longer rides on `channel_migration`
                alone. It keeps its slot after IG either way. */}
            {channelMigration || enableLineChannelCreation ? (
              <FormControlLabel
                value="line"
                control={<Radio />}
                label={
                  <ChannelRadioGroupOption
                    channelType="line"
                    title={t("channels.addModal.body.channel.line.title")}
                    description={
                      enableLineChannelCreation
                        ? t(
                            "channels.addModal.body.channel.line.selfServeDescription",
                            "Enter the Channel ID and Channel secret to connect your LINE Official Account",
                          )
                        : t("channels.addModal.body.channel.line.description")
                    }
                  />
                }
              />
            ) : null}
            <FormControlLabel
              value="whatsapp"
              control={<Radio />}
              label={
                <ChannelRadioGroupOption
                  channelType="whatsapp"
                  title="Whatsapp"
                />
              }
            />
            {enableEmailChannels && (
              <FormControlLabel
                value="email"
                control={<Radio />}
                label={
                  <ChannelRadioGroupOption
                    channelType="email"
                    title={t("channels.addModal.body.channel.email.title")}
                    description={t(
                      "channels.addModal.body.channel.email.description",
                      "使用品牌網域建立 Email 渠道，完成 DNS 驗證即可開始發信",
                    )}
                  />
                }
              />
            )}
            <FormControlLabel
              value="wccs"
              control={<Radio />}
              label={
                <ChannelRadioGroupOption
                  channelType="wccs"
                  title={t("channels.addModal.body.channel.wccs.title")}
                  description={t(
                    "channels.addModal.body.channel.wccs.description",
                  )}
                />
              }
            />
            {enableWebstream && (
              <FormControlLabel
                value="web-streams"
                control={<Radio />}
                label={
                  <ChannelRadioGroupOption
                    channelType="web-streams"
                    title={t("channels.addModal.body.channel.webStreams.title")}
                    description={t(
                      "channels.addModal.body.channel.webStreams.description",
                    )}
                  />
                }
              />
            )}
          </RadioGroup>
        </FormControl>
        {channelMigration ? null : (
          <Typography variant="body" color={theme.colors.staticFgBody}>
            {t("channels.description")}
          </Typography>
        )}
      </Stack>
    </Modal>
  );
};

const iconStyles = defineStyles({
  icon: css({
    fontSize: "40px",
  }),
  wccs: css({
    color: theme.colors.staticFgPrimary,
  }),
});

const ChannelRadioGroupOption: React.FC<{
  channelType: ChannelTypes;
  title: string;
  titleBadge?: React.ReactNode;
  description?: string;
}> = ({ channelType, title, titleBadge, description }) => {
  return (
    <Stack direction="row" gap="10px" alignItems="center">
      {channelType === "web-streams" ? (
        <ChannelIcon type="wccs" isWebStream sx={iconStyles.icon} />
      ) : (
        <ChannelIcon type={channelType} sx={iconStyles.icon} />
      )}
      <Stack gap="4px">
        <Stack direction="row" gap="8px" alignItems="center">
          <Typography
            variant="body"
            fontWeight={700}
            color={theme.colors.staticFgBody}
          >
            {title}
          </Typography>
          {titleBadge}
        </Stack>
        {description ? (
          <Typography variant="body" color={theme.colors.staticFgNote}>
            {description}
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
};
